// Drives the page's registered `tableController` from a proposal's GROUNDED changes — the same idea as
// navExecutor (call the app's real handlers, never a DOM click). The controller is provided by the page
// that mounts Otto (the Recommendations container binds its own sort/hide/pin/pagination/reset/export
// handlers to this shape). Best-effort PER change: one op that throws (or a controller missing that method)
// never blocks the others, exactly like the chart action queue and navExecutor — a manipulation must never
// crash the chat. Returns a promise of { applied, outcomes } (see the outcome contract below).
//
// The tableController contract every table page implements:
//   sort(columnKey, direction)      direction: 'asc' | 'desc' | null (null = toggle)
//   setColumnVisible(columnKey, on) true = show, false = hide
//   pin(columnKey, on)              true = pin, false = unpin
//   unpinAll()
//   setPageSize(n)                  n is one of the supported sizes
//   resetSort()
//   resetLocalFilters()             the page's LOCAL filters (+ local sort)
//   resetTableConfig()              columns back to the page default
//   export(format, mode, opts)      format: 'excel' | 'word' | 'pdf'; mode: 'simple' | 'advanced';
//                                   opts: { includeSoftCost }. May return (a promise of) an OUTCOME — below.
//   exportVariant(key, opts)        run one NAMED export variant (a chooser entry) directly; opts =
//                                   { format, breakdown, includeSoftCost }. Same outcome contract.
//   setColumnFilter(colKey, names)  apply a discrete option-value row-filter
//   setColumnWildcard(colKey, spec) apply a numeric/text threshold row-filter { operator, value }
//   clearColumnFilter(colKey)       clear one column's row-filter
//   getContext()                    -> { columns, sort, pagination } (grounding surface; read elsewhere)
//   search(text)                    the page's global search box; also clears it when called with ""
//   sortRecent(axis)                "created" | "edited" — the grid's own Newly Created/Newly Edited sort
//   gotoColumn(colKey)              scroll to + highlight a column, no data change
//   resetAll()                      the page's single "reset everything" action (filters AND sort)
//   openFilters()                   open the page's filter panel/popup
//   openAppliedFilters()            open the page's "currently applied filters" summary/popup
// The six above are VIEW-SHAPING and page-declared (controller.availableActions()) — the proposal already
// refuses a change whose action is missing from availableActions, so by the time a change reaches CALLS the
// controller is expected to have the matching method.

const CALLS = {
    sort: (c, ch) => c.sort(ch.column.key, ch.value || null),
    hide_column: (c, ch) => c.setColumnVisible(ch.column.key, false),
    show_column: (c, ch) => c.setColumnVisible(ch.column.key, true),
    pin_column: (c, ch) => c.pin(ch.column.key, true),
    unpin_column: (c, ch) => c.pin(ch.column.key, false),
    unpin_all: c => c.unpinAll(),
    set_page_size: (c, ch) => c.setPageSize(parseInt(ch.value, 10)),
    reset_sort: c => c.resetSort(),
    reset_filters: c => c.resetLocalFilters(),
    reset_table: c => c.resetTableConfig(),
    // "variant" runs ONE named chooser entry (Normal Grouping / CrossWalk / Criticality-Based) with its
    // resolved axes; "simple"/"advanced" keep the original path. includeSoftCost rides along on both.
    export: (c, ch) =>
        ch.mode === "variant"
            ? c.exportVariant(ch.variant, { format: ch.value, breakdown: ch.breakdown || null, includeSoftCost: !!ch.includeSoftCost })
            : c.export(ch.value, ch.mode || "simple", { includeSoftCost: !!ch.includeSoftCost }),
    // Row filtering: the proposal resolved the change to option NAMES (mode "options") or a { operator, value }
    // threshold (mode "wildcard"), grounded against the column's real data before this ever runs.
    column_filter: (c, ch) =>
        ch.mode === "wildcard"
            ? c.setColumnWildcard(ch.column.key, { operator: ch.operator, value: ch.value })
            : c.setColumnFilter(ch.column.key, ch.values || []),
    clear_column_filter: (c, ch) => c.clearColumnFilter(ch.column.key),
    // View-shaping: change what is SHOWN. `clear_search` is `search` with an empty string — one page handler,
    // two utterances, so a page cannot wire one without the other.
    search: (c, ch) => c.search(ch.value || ""),
    clear_search: c => c.search(""),
    sort_recent: (c, ch) => c.sortRecent(ch.value),
    goto_column: (c, ch) => c.gotoColumn(ch.column.key),
    reset_all: c => c.resetAll(),
    open_filters: c => c.openFilters(),
    open_applied_filters: c => c.openAppliedFilters()
};

// An export is the one table op whose RESULT the user needs to hear about: the report backend either returns
// the file inline or queues a background job (the row threshold is server-side, so nobody can predict which),
// and "check Export History" is only true in the second case. So `export` may return an outcome — a plain
// object or a promise of one — which the card then reports verbatim rather than guessing:
//   { status: "downloaded" }  the file came back and was saved
//   { status: "queued" }      too big to build inline; it's a background job (-> Export History)
//   { status: "opened" }      the page's export chooser was opened; the user picks from here
//   { status: "error", message? }
// A controller that returns nothing is treated as "downloaded" (the legacy fire-and-forget behaviour), so a
// page that hasn't been taught to report still works.
const DEFAULT_OUTCOME = { status: "downloaded" };
const REPORTING_ACTIONS = new Set(["export"]);

const normalizeOutcome = value => {
    if (!value || typeof value !== "object" || !value.status) return DEFAULT_OUTCOME;
    return value;
};

// Applies every change SYNCHRONOUSLY (React batching + the grids' functional-updater merges depend on this —
// see building/index.jsx `_ottoApplyParams`), then resolves once any reported outcomes settle. The returned
// promise never rejects: a thrown op or a rejected outcome becomes an `error` outcome so one bad op can't
// break the rest or the chat.
export const executeTableCommand = (changes, controller) => {
    if (!controller || !Array.isArray(changes)) return Promise.resolve({ applied: 0, outcomes: [] });
    let applied = 0;
    const pending = [];
    for (const ch of changes) {
        const call = ch && CALLS[ch.action];
        if (!call) continue;
        try {
            const result = call(controller, ch);
            applied += 1;
            if (REPORTING_ACTIONS.has(ch.action)) {
                pending.push(
                    Promise.resolve(result)
                        .then(r => ({ action: ch.action, mode: ch.mode || "simple", ...normalizeOutcome(r) }))
                        .catch(e => ({ action: ch.action, mode: ch.mode || "simple", status: "error", message: (e && e.message) || null }))
                );
            }
        } catch (e) {
            /* best-effort: one failed op (or a controller missing that method) must not break the rest */
            if (REPORTING_ACTIONS.has(ch.action)) {
                pending.push(Promise.resolve({ action: ch.action, mode: ch.mode || "simple", status: "error", message: (e && e.message) || null }));
            }
        }
    }
    return Promise.all(pending).then(outcomes => ({ applied, outcomes }));
};
